/**
 * Ticket Lifecycle v2 — Slice 4F
 *
 * Wrapper recorder for **advanced** advisor work-product events
 * (estimate / quote / customer approval / parts / invoice). Distinct
 * from the slice-4B custody-driven capture, which fires
 * `ticket_accepted` and `kicked_back_to_tech` on every successful
 * custody transition.
 *
 * Gating contract (BOTH must be on for an emit to fire):
 *   1. `advisorAdvancedActionCapture` — slice-4F flag, default OFF in
 *      every preset. Internal-admin opt-in via /feature-flags.
 *   2. `advisorTimingAnalytics` — slice-1 flag also default OFF;
 *      enforced transitively by the underlying `useRecordAdvisorAction`
 *      hook. We do not re-check it here; the inner recorder is the
 *      single source of truth for the existing recorder behavior.
 *
 * When either flag is off, the returned function is a no-op promise.
 * Callers can `await` it without conditional branches.
 *
 * Slice 4F intentionally ships only this foundation. No emit sites are
 * wired yet — the per-site wiring (where to emit, which action_kind,
 * how to resolve `ticket_id`) is deferred to a future sub-slice that
 * can also be manually validated.
 *
 * ─── Candidate emit sites (deferred; documented for future slice) ───
 *
 *   • `useFinalizeInvoice` (src/hooks/invoicing/useCustomerInvoiceMutations.ts:77)
 *     Semantically: `invoice_sent`. Blocker: takes only `invoiceId`;
 *     needs a small lookup or an optional `ticketId` arg.
 *
 *   • `useRecordAuthorization` (same file:49)
 *     Semantically: `customer_approval_received` (customer signed).
 *     Blocker: same `invoiceId`-only signature.
 *
 *   • `useCreateCustomerInvoice` (same file:29)
 *     Has `ticketId` directly. Action semantically AMBIGUOUS between
 *     `estimate_started` and `estimate_completed`. Product decision
 *     required before wiring.
 *
 *   • `useDviSession.addApprovedJobs` (src/hooks/useDviSession.ts:131)
 *     Customer-decision flow. Could map to `customer_approval_received`
 *     for the per-finding approve action, but the customer-facing
 *     `dvi-customer-respond` path is the more direct emit point.
 *     Needs product clarification on whether to instrument advisor-side
 *     bulk-approve or customer-side single-approve.
 *
 *   • Parts ordering surfaces — not yet identified in this slice;
 *     parts-ordering UI exists but the canonical "Order Parts" action
 *     hook needs to be located before instrumentation.
 *
 *   • Customer contact / phone log surfaces — OCIS-style event timeline
 *     exists; no single "Log contact" mutation. Needs product
 *     clarification on which existing OCIS path represents
 *     `customer_contact_attempted` / `phone_contact_logged`.
 *
 * Boundary: this module imports neither scheduling, timer, parking,
 * rollover, bay, nor invariants code. It is a thin wrapper over
 * `useRecordAdvisorAction`.
 */
import { useCallback } from 'react';
import { useFeatureFlag } from '@/hooks/useFeatureFlags';
import {
  useRecordAdvisorAction,
  type RecordAdvisorActionArgs,
} from '@/hooks/tickets/useRecordAdvisorAction';
import { useImplicitAdvisorTakeover } from '@/hooks/tickets/useImplicitAdvisorTakeover';

/**
 * Returns a `record` function callers can invoke fire-and-forget.
 * No-op when `advisorAdvancedActionCapture` is off. Otherwise
 * delegates to `useRecordAdvisorAction`, which additionally requires
 * `advisorTimingAnalytics` to be on.
 *
 * Callers must not use the returned promise as a control-flow signal;
 * it always resolves (errors surface via console.error inside the
 * inner recorder).
 */
export function useRecordAdvancedAdvisorAction() {
  const advisorAdvancedCaptureEnabled = useFeatureFlag(
    'advisorAdvancedActionCapture',
  );
  const recordAdvisorAction = useRecordAdvisorAction();
  // Slice 5D-5: after a successful advanced advisor action emit, the
  // hook also attempts an implicit advisor takeover. The takeover
  // self-gates on `advisorImplicitTakeover` + the action-kind
  // allow-list + role / custody / actor checks — when any guard fails
  // it silently skips. The composition keeps "recorded the advisor
  // work" and "updated who owns the ticket" in one place.
  const tryImplicitTakeover = useImplicitAdvisorTakeover();

  return useCallback(
    async (args: RecordAdvisorActionArgs): Promise<void> => {
      // Both flags must be on. The outer gate short-circuits when the
      // advanced flag is off so we don't even consult the inner gate;
      // the inner recorder is the canonical authority for the existing
      // `advisorTimingAnalytics` check when this layer is permitted.
      if (!advisorAdvancedCaptureEnabled) return;
      await recordAdvisorAction(args);
      // Slice 5D-5: fire-and-forget implicit takeover. Always resolves
      // (the hook self-catches). Sequenced AFTER the advisor action
      // emit so the action event row is in the DB before the takeover
      // custody event row — analytics see "advisor did X" then
      // "ownership flipped." Non-blocking: a takeover failure cannot
      // affect the caller's primary mutation success path.
      void tryImplicitTakeover({
        ticketId: args.ticketId,
        actionKind: args.actionKind,
      });
    },
    [advisorAdvancedCaptureEnabled, recordAdvisorAction, tryImplicitTakeover],
  );
}
